import React, { useMemo, useState } from 'react';
import styles from '../CanvassInfo.module.css';
import { Button } from '../../layout/buttons';
import { Modal } from '../../layout/Modal';
import { logger } from '../../../utils/logger';

/**
 * ObjectivesSection
 * Shows Objective and Sub Objectives. Edit opens a modal with individual fields.
 *
 * Props:
 * - groupedInsights: object (from CanvassInfo)
 * - onSaveCanvassPatch: (patch: { objective?: string, sub_objectives?: string[] }) => Promise<void>
 */
export default function ObjectivesSection({ groupedInsights, onSaveCanvassPatch, isEditable = true }) {
  const { objectiveText, subIndices, subGroups } = useMemo(() => {
    // Objective now comes tagged as 'research_objective' with a string value
    const objArr = groupedInsights.research_objective || [];
    const objText = objArr
      .map((it) => safeToString(it?.value))
      .filter(Boolean)
      .join('; ');

    const subAll = groupedInsights.sub_objective || [];
    const groups = {};
    subAll.forEach((item, idx) => {
      if (typeof item === 'string') {
        // New schema: direct list of strings, one per sub-objective
        const key = String(idx);
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
        return;
      }
      // Legacy fallback: items with a key like sub_0_title
      const keyStr = String(item?.key || '');
      let m = keyStr.match(/^sub_(\d+)_/);
      if (!m && /^sub_(title|correlation|why_it_matters|context_trigger|emotions)$/.test(keyStr)) {
        m = ['match', '0'];
      }
      if (m) {
        const legacyIdx = m[1];
        if (!groups[legacyIdx]) groups[legacyIdx] = [];
        groups[legacyIdx].push(item);
      }
    });
    const indices = Object.keys(groups).sort((a, b) => Number(a) - Number(b));

    return { objectiveText: objText, subIndices: indices, subGroups: groups };
  }, [groupedInsights]);

  const [open, setOpen] = useState(false);
  const [objectiveEdit, setObjectiveEdit] = useState('');
  const [subsEdit, setSubsEdit] = useState([]); // [{idx, text}]
  const [saving, setSaving] = useState(false);

  const openModal = () => {
    // Prefill objective as a single string
    const obj = groupedInsights?.research_objective?.[0]?.value;
    setObjectiveEdit(safeToString(obj) || '');
    const edits = subIndices.map((idx) => {
      const items = subGroups[idx] || [];
      let text = '';
      if (items.length && typeof items[0] === 'string') {
        // New schema: items is [string]
        text = safeToString(items[0]);
      } else {
        // Legacy: pick title if present, else fallback
        const titleItem = items.find((it) => String(it.key).includes('title'));
        const legacyCtx = items.find((it) => String(it.key).includes('context_trigger'));
        text = titleItem
          ? safeToString(titleItem.value)
          : legacyCtx
            ? safeToString(legacyCtx.value)
            : items.map((it) => safeToString(it.value)).filter(Boolean).join('. ');
      }
      return { idx, text };
    });
    setSubsEdit(edits);
    setOpen(true);
  };

  const onSave = async () => {
    try {
      setSaving(true);
      const patch = {};
      if (typeof onSaveCanvassPatch === 'function') {
        patch.objective = (objectiveEdit || '').trim();
        // Build sub_objectives as an ordered string array (by idx)
        patch.sub_objectives = subsEdit
          .map((s) => (s.text || '').trim())
          .filter(Boolean);
        await onSaveCanvassPatch(patch);
      }
      setOpen(false);
    } catch (e) {
      logger.error('Failed to save objectives:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <section className={styles.section + ' ' + styles.sectionFirst}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>Objective</div>
          {isEditable && (
            <a className={styles.editLink} type="button" onClick={openModal}>Edit</a>
          )}
        </div>
        <div className={styles.paragraph}>{objectiveText || 'Objective has yet to be added.'}</div>
      </section>
      <div className={styles.modalSeparator} />
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>Sub Objectives</div>
        </div>
        {subIndices.length > 0 ? (
          subIndices.map((idx) => {
            const items = subGroups[idx] || [];
            let text = '';
            
            // Handle different data structures for sub objectives
            if (items.length === 0) {
              text = '';
            } else if (items.length === 1) {
              const item = items[0];
              // If it's a string, use it directly
              if (typeof item === 'string') {
                text = safeToString(item);
              }
              // If it's an object with title field, use the title
              else if (typeof item === 'object' && item !== null && item.title) {
                text = safeToString(item.title);
              }
              // If it's an object with value field
              else if (typeof item === 'object' && item !== null && item.value) {
                text = safeToString(item.value);
              }
              // If it's an object with key that includes 'title'
              else if (typeof item === 'object' && item !== null && item.key && String(item.key).includes('title')) {
                text = safeToString(item.value);
              }
              // Fallback: convert object to string representation
              else {
                text = safeToString(item);
              }
            } else {
              // Multiple items: look for title first, then fallback
              const titleItem = items.find((it) => {
                if (typeof it === 'object' && it !== null) {
                  return it.title || (it.key && String(it.key).includes('title'));
                }
                return false;
              });
              
              if (titleItem) {
                text = safeToString(titleItem.title || titleItem.value);
              } else {
                // Fallback: join all non-empty values
                text = items.map((it) => {
                  if (typeof it === 'string') return it;
                  if (typeof it === 'object' && it !== null) {
                    return it.value || it.title || '';
                  }
                  return safeToString(it);
                }).filter(Boolean).join('. ');
              }
            }
            
            return (
              <p key={idx} className={styles.paragraph}>{text || '—'}</p>
            );
          })
        ) : (
          <p className={styles.paragraph}>Sub Objectives have yet to be added.</p>
        )}
      </section>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Edit Objectives"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, width: '100%' }}>
            <Button variant="outline-neutral" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" disabled={saving} onClick={onSave}>{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        }
      >
        <div>
          <div style={{ marginBottom: 8, color: 'var(--text-secondary, #6c757d)' }}>Objective</div>
          <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
            <div>
              <textarea
                value={objectiveEdit}
                onChange={(e) => setObjectiveEdit(e.target.value)}
                rows={2}
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #e5e7eb' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 8, color: 'var(--text-secondary, #6c757d)' }}>Sub Objectives</div>
          <div style={{ display: 'grid', gap: 12 }}>
            {subsEdit.map((s, i) => (
              <div key={s.idx} style={{ position: 'relative' }}>
                <textarea
                  value={s.text}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSubsEdit((prev) => prev.map((p) => (p.idx === s.idx ? { ...p, text: val } : p)));
                  }}
                  rows={3}
                  style={{ width: '100%', padding: '10px 40px 10px 10px', borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
                <button
                  onClick={() => {
                    setSubsEdit((prev) => prev.map((p) => (p.idx === s.idx ? { ...p, text: '' } : p)));
                  }}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#c82333';
                    e.target.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#dc3545';
                    e.target.style.transform = 'scale(1)';
                  }}
                  title="Delete subobjective"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </>
  );
}

function safeToString(v) {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.join(', ');
  try {
    return String(v);
  } catch {
    return '';
  }
}
